import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Sparkles, FileText, Layers, RefreshCw } from 'lucide-react'
import { ReflectionTree } from '@/components/memory/ReflectionTree'
import type { ReflectionNode } from '@/components/memory/ReflectionTree'
import { ImportanceBadge } from '@/components/memory/ImportanceBadge'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'

type MemoryTypeFilter = 'all' | 'reflection' | 'observation_evidence' | 'pass_output' | 'pre_read_summary' | 'validation_outcome'

interface MemoryItem {
  memory_id: string
  content_text: string
  memory_type: string
  importance_raw: number
  reflection_depth: number
  created_at: number
  source_type?: string
  cited_memory_ids?: string[]
}

interface MemoryResponse {
  memories: MemoryItem[]
  total: number
}

interface ReflectResponse {
  deferred: boolean
  deferred_reason?: string
  reflections: Array<{ memory_id: string; content_text: string }>
}

const TYPE_LABELS: Record<string, string> = {
  reflection: 'Reflection',
  observation_evidence: 'Evidence',
  pass_output: 'Pass Output',
  pre_read_summary: 'Pre-Read',
  validation_outcome: 'Validation',
  plan: 'Plan',
}

function typeIcon(type: string) {
  if (type === 'reflection') return <Sparkles className="h-3.5 w-3.5 text-signal-cyan" />
  if (type === 'pre_read_summary') return <Layers className="h-3.5 w-3.5 text-echo-indigo" />
  return <FileText className="h-3.5 w-3.5 text-muted-foreground" />
}

export function MemoryStreamPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const [memories, setMemories] = useState<MemoryItem[]>([])
  const [reflections, setReflections] = useState<ReflectionNode[]>([])
  const [total, setTotal] = useState(0)
  const [filter, setFilter] = useState<MemoryTypeFilter>('all')
  const [loading, setLoading] = useState(true)
  const [reflecting, setReflecting] = useState(false)
  const [reflectMsg, setReflectMsg] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'feed' | 'tree'>('feed')

  const token = localStorage.getItem('echo_session') ?? ''
  const headers = { Authorization: `Bearer ${token}` }

  function fetchMemories() {
    if (!projectId) return
    setLoading(true)
    const params = new URLSearchParams({ top_k: '50' })
    if (filter !== 'all') params.set('memory_types', filter)
    fetch(`/api/v1/projects/${projectId}/memories?${params}`, { headers })
      .then(r => r.json() as Promise<MemoryResponse>)
      .then((data) => {
        setMemories(data.memories ?? [])
        setTotal(data.total ?? 0)
      })
      .catch(() => setMemories([]))
      .finally(() => setLoading(false))
  }

  function fetchReflections() {
    if (!projectId) return
    fetch(`/api/v1/projects/${projectId}/reflections`, { headers })
      .then(r => r.json() as Promise<{ reflections?: ReflectionNode[] }>)
      .then((data) => setReflections(data.reflections ?? []))
      .catch(() => setReflections([]))
  }

  useEffect(() => { fetchMemories() }, [projectId, filter])
  useEffect(() => { fetchReflections() }, [projectId])

  async function triggerReflection() {
    if (!projectId) return
    setReflecting(true)
    setReflectMsg(null)
    try {
      const res = await fetch(`/api/v1/projects/${projectId}/reflect`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      })
      const data = await res.json() as ReflectResponse
      if (data.deferred) {
        setReflectMsg(data.deferred_reason ?? 'Reflection deferred.')
      } else {
        setReflectMsg(`Generated ${data.reflections?.length ?? 0} new reflection${data.reflections?.length !== 1 ? 's' : ''}.`)
        fetchReflections()
        fetchMemories()
      }
    } catch {
      setReflectMsg('Reflection failed. Try again shortly.')
    } finally {
      setReflecting(false)
    }
  }

  const filterButtons: Array<{ key: MemoryTypeFilter; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'reflection', label: 'Reflections' },
    { key: 'observation_evidence', label: 'Evidence' },
    { key: 'pass_output', label: 'Pass Outputs' },
    { key: 'pre_read_summary', label: 'Pre-Reads' },
    { key: 'validation_outcome', label: 'Validations' },
  ]

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-deep-ink flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-signal-cyan" />
            Memory Stream
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {total} memories indexed for this project
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={triggerReflection}
          disabled={reflecting}
          className="shrink-0"
        >
          <RefreshCw className={cn('h-3.5 w-3.5 mr-1.5', reflecting && 'animate-spin')} />
          {reflecting ? 'Reflecting…' : 'Reflect now'}
        </Button>
      </div>

      {reflectMsg && (
        <div className="rounded-lg border border-border-gray bg-soft-panel px-4 py-2 text-sm text-deep-ink">
          {reflectMsg}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border-gray">
        {(['feed', 'tree'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab
                ? 'border-echo-indigo text-echo-indigo'
                : 'border-transparent text-muted-foreground hover:text-deep-ink'
            )}
          >
            {tab === 'feed' ? 'Memory Feed' : 'Reflection Tree'}
          </button>
        ))}
      </div>

      {activeTab === 'feed' && (
        <>
          {/* Type filter */}
          <div className="flex gap-1.5 flex-wrap">
            {filterButtons.map(fb => (
              <button
                key={fb.key}
                onClick={() => setFilter(fb.key)}
                className={cn(
                  'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                  filter === fb.key
                    ? 'bg-echo-indigo text-white border-echo-indigo'
                    : 'bg-white text-muted-foreground border-border-gray hover:border-echo-indigo/50'
                )}
              >
                {fb.label}
              </button>
            ))}
          </div>

          {/* Memory list */}
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-20 rounded-lg bg-soft-panel animate-pulse" />
              ))}
            </div>
          ) : memories.length === 0 ? (
            <div className="rounded-lg border border-border-gray p-8 text-center text-sm text-muted-foreground">
              No memories yet for this filter. Upload data to start building the memory stream.
            </div>
          ) : (
            <div className="space-y-2">
              {memories.map(m => (
                <div
                  key={m.memory_id}
                  className={cn(
                    'rounded-lg border p-3 space-y-1.5',
                    m.memory_type === 'reflection'
                      ? 'border-signal-cyan/30 bg-signal-cyan/5'
                      : 'border-border-gray bg-white'
                  )}
                >
                  <div className="flex items-start gap-2">
                    {typeIcon(m.memory_type)}
                    <p className="text-sm text-deep-ink leading-snug">{m.content_text}</p>
                  </div>
                  <div className="flex items-center gap-2 pl-5">
                    <span className="text-xs text-muted-foreground font-medium">
                      {TYPE_LABELS[m.memory_type] ?? m.memory_type}
                    </span>
                    <ImportanceBadge score={m.importance_raw} />
                    {m.reflection_depth > 0 && (
                      <span className="text-xs text-muted-foreground">depth {m.reflection_depth}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'tree' && (
        <ReflectionTree nodes={reflections} />
      )}
    </div>
  )
}
